import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Pencil, Save, X, Eye, ChevronLeft, ChevronRight, List, Table, GripVertical, Download, SlidersHorizontal, FilterX, ListFilter } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';
import { applySearch, applyColumnFilters, applySort, hasColumnFilters, resolveSelection, NO_MATCH_LIST, SEARCH_HINT } from '../../utils/listSearch';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import NoMatchState from '../../components/common/NoMatchState';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import StuiSelect from '../../components/common/StuiSelect';

// Editable roles — SysAdmin is displayed separately as a synthetic
// always-Grant column (a20 rework 2026-08-07). The IsSysAdmin flag on
// AppSec_Users is the sole SysAdmin mechanism; DefRoles has no SysAdmin
// rows. We render the column purely so admins can see the full picture
// on this page.
// What search reads, labelled as the user sees it. The labels double as field: prefixes and feed
// the Advanced Search builder's field list.
const SEARCH_FIELD_LABELS = [
  { key: 'displayName', label: 'Display Name' }, { key: 'permissionKey', label: 'Permission Key' },
  { key: 'category', label: 'Category' }, { key: 'grantScope', label: 'Grant Scope' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

const ROLE_NAMES         = ['Guest', 'User', 'PowerUser', 'AppAdmin'];
const DISPLAY_ROLE_NAMES = [...ROLE_NAMES, 'SysAdmin'];
const isSynthetic = (roleName) => roleName === 'SysAdmin';

const RolesCapabilitiesPage = () => {
  const [matrix, setMatrix] = useState(null);
  const [, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  // List view state
  const [picked, setSelected] = useState(null);
  const [gridSort, setGridSort] = useState(null); // header sort, shared by both views
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dirtyRoles, setDirtyRoles] = useState({});

  // Datasheet state
  const [sheetEditing, setSheetEditing] = useState(false);
  const [dirtySheet, setDirtySheet] = useState({});
  const [gridFilters, setGridFilters] = useState({});
  const [columnOrder, setColumnOrder] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [matrixRes, permRes] = await Promise.all([
        adminManagementApi.getRoleMatrix(),
        adminManagementApi.getPermissions()
      ]);
      setMatrix(matrixRes.data);
      setPermissions(permRes.data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ permId: selected.id });
    setDirtyRoles({});
  };

  const cancelEdit = (force = false) => {
    if (!force && (Object.keys(dirtyRoles).length > 0 || Object.keys(dirtySheet).length > 0)) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyRoles({}); setSheetEditing(false); setDirtySheet({}); });
      return;
    }
    setEditing(null);
    setDirtyRoles({});
    setSheetEditing(false);
    setDirtySheet({});
  };

  // A Global permission is granted per user in AppSec_GlobalPermissions. A role is only
  // ever held per database, so a role default could never produce that grant — the row
  // stays listed here (hiding it reads as a deleted permission) but offers no Grant/Deny.
  // SysAdmin is unaffected: the flag bypasses scope entirely.
  const isGlobalOnly = (perm) => perm?.grantScope === 'Global';
  const GLOBAL_ONLY_LABEL = 'Global only';

  const getRoleAccess = (roleName) => {
    if (isSynthetic(roleName)) return 'Grant';                 // SysAdmin bypass — always Grant
    if (dirtyRoles[roleName] !== undefined) return dirtyRoles[roleName];
    return selected?.roles[roleName] || 'None';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (viewMode === 'datasheet') {
        const updates = Object.entries(dirtySheet).map(([key, accessType]) => {
          const [role, permId] = key.split('-');
          return { Role: role, PermissionDefId: parseInt(permId), AccessType: accessType };
        });
        if (updates.length > 0) await adminManagementApi.updateRoleMatrix(updates);
        setDirtySheet({});
        setSheetEditing(false);
      } else if (editing && Object.keys(dirtyRoles).length > 0) {
        const updates = Object.entries(dirtyRoles).map(([role, access]) => ({
          Role: role, PermissionDefId: selected.id, AccessType: access
        }));
        await adminManagementApi.updateRoleMatrix(updates);
        setEditing(null);
        setDirtyRoles({});
      }
      await load();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSheetClick = (permId, roleName, currentAccess) => {
    if (!sheetEditing) return;
    if (isSynthetic(roleName)) return;                         // SysAdmin column is read-only
    const key = `${roleName}-${permId}`;
    const next = currentAccess === 'Grant' ? 'None' : 'Grant';
    const original = matrix.permissions.find(p => p.id === permId)?.roles[roleName] || 'None';
    if (next === original) {
      const n = { ...dirtySheet };
      delete n[key];
      setDirtySheet(n);
    } else {
      setDirtySheet({ ...dirtySheet, [key]: next });
    }
  };

  const getSheetAccess = (permId, roleName) => {
    if (isSynthetic(roleName)) return 'Grant';                 // SysAdmin bypass — always Grant
    const key = `${roleName}-${permId}`;
    if (dirtySheet[key] !== undefined) return dirtySheet[key];
    return matrix?.permissions?.find(p => p.id === permId)?.roles[roleName] || 'None';
  };

  const accessStyle = (access) => ({
    fontWeight: 600,
    color: access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)'
  });

  const isEditing = editing !== null || sheetEditing;
  const hasDirty = Object.keys(dirtyRoles).length > 0 || Object.keys(dirtySheet).length > 0;

  const searchedPerms = applySearch(matrix?.permissions || [], searchTerm, SEARCH_FIELDS, SEARCH_FIELD_LABELS);

  // Transform matrix data into flat rows for FilterableDataGrid.
  // SysAdmin is a synthetic always-Grant column (getSheetAccess handles it).
  const searchedGridData = searchedPerms.map(p => {
    const row = { id: p.id, category: p.category, displayName: p.displayName || p.permissionKey, permissionKey: p.permissionKey, grantScope: p.grantScope };
    DISPLAY_ROLE_NAMES.forEach(r => {
      row[r] = (isGlobalOnly(p) && !isSynthetic(r))
        ? GLOBAL_ONLY_LABEL
        : (getSheetAccess(p.id, r) === 'Grant' ? 'Grant' : 'Deny');
    });
    return row;
  });

  // The column filters are expressed in GRID-ROW space — their keys include the per-role columns
  // (Guest, User, …) which exist only on the mapped row, not on the permission object. So the
  // reduction happens here and is then projected back onto the permission objects that list view
  // iterates, which is what puts both views on one dataset. `gridFilters` was previously handed to
  // the grid and applied nowhere, so filtering by role never reached list view at all.
  // The header sort is in the same grid-row space, so it is applied here and its order projected
  // back too — list view follows the datasheet's sort.
  const gridData   = applySort(applyColumnFilters(searchedGridData, gridFilters), gridSort);
  const rowOrder   = new Map(gridData.map((r, i) => [r.id, i]));
  const filteredPerms = (hasColumnFilters(gridFilters) || gridSort)
    ? searchedPerms.filter(p => rowOrder.has(p.id)).sort((a, b) => rowOrder.get(a.id) - rowOrder.get(b.id))
    : searchedPerms;

  // What the detail pane shows. Never a row the list is not showing — see resolveSelection. Being
  // derived from the reloaded matrix, it also picks up fresh role values after a save.
  const selected = resolveSelection(filteredPerms, picked, { keep: !!editing });

  // Feeds the filter dropdowns only. Uses the PERSISTED role values rather than pending sheet
  // edits on purpose, so the option list does not shift while a change is uncommitted.
  const allGridData = searchedPerms.map(p => {
    const row = { id: p.id, category: p.category, displayName: p.displayName || p.permissionKey, permissionKey: p.permissionKey, grantScope: p.grantScope };
    DISPLAY_ROLE_NAMES.forEach(r => {
      row[r] = isSynthetic(r) ? 'Grant'
        : isGlobalOnly(p) ? GLOBAL_ONLY_LABEL
        : ((p.roles[r] || 'None') === 'Grant' ? 'Grant' : 'Deny');
    });
    return row;
  });

  const gridColumns = [
    { key: 'category', label: 'Category', width: 120 },
    { key: 'displayName', label: 'Display Name', width: 200 },
    { key: 'permissionKey', label: 'Permission Key', width: 260 },
    ...DISPLAY_ROLE_NAMES.map(r => ({ key: r, label: r, width: 110 }))
  ];

  const renderEditableCell = (row, column, value, isEdited) => {
    if (!DISPLAY_ROLE_NAMES.includes(column.key)) return <span className="stui-text-italic">{value}</span>;
    const permId = row.id;
    const roleName = column.key;
    const access = getSheetAccess(permId, roleName);
    const key = `${roleName}-${permId}`;
    const isDirty = dirtySheet[key] !== undefined;
    const synthetic = isSynthetic(roleName);
    const globalOnly = row.grantScope === 'Global' && !synthetic;
    if (globalOnly) {
      return (
        <span
          title="Granted per user under Global Permissions. A role default cannot grant it — a role is only ever held on one database."
          className="stui-text-italic"
          style={{ display: 'block', textAlign: 'center' }}
        >
          {GLOBAL_ONLY_LABEL}
        </span>
      );
    }
    return (
      <span
        onClick={sheetEditing && !synthetic ? (e) => { e.stopPropagation(); handleSheetClick(permId, roleName, access); } : undefined}
        title={synthetic ? 'SysAdmin bypass — cannot be edited' : undefined}
        style={{
          cursor: sheetEditing && !synthetic ? 'pointer' : 'default',
          fontWeight: 600,
          color: access === 'Grant' ? 'var(--color-success)' : 'var(--color-error)',
          backgroundColor: isDirty ? 'var(--color-dirty-bg)' : undefined,
          opacity: synthetic ? 0.75 : 1,
          display: 'block',
          textAlign: 'center'
        }}
      >
        {access === 'Grant' ? 'Grant' : 'Deny'}
      </span>
    );
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <button className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} onClick={() => { setViewMode('list'); cancelEdit(); }} title="List view"><List size={18} /></button>
          <button className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} onClick={() => { setViewMode('datasheet'); setEditing(null); setSelected(null); setDirtyRoles({}); }} title="Datasheet view"><Table size={18} /></button>
          <span className="stui-module-header-meta">
            {filteredPerms.length !== (matrix?.permissions?.length || 0)
              ? `${filteredPerms.length} of ${matrix?.permissions?.length || 0} permissions`
              : `${matrix?.permissions?.length || 0} permissions`
            }
          </span>
        </div>
        <div className="stui-module-header-actions">
          {/* CRUD: no + or trash on this page — permissions are managed on the Permissions page */}
          {viewMode === 'list' ? (
            <button className={`stui-crud-btn ${isEditing ? '' : !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
              {isEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
          ) : (
            <button className={`stui-crud-btn`} onClick={sheetEditing ? cancelEdit : () => setSheetEditing(true)} title={sheetEditing ? 'Cancel' : 'Edit'}>
              {sheetEditing ? <X size={18} /> : <Pencil size={18} />}
            </button>
          )}
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          {/* Clear filter — required now that the column filters reduce BOTH views: without it an
              active role filter would hide rows from list view with no way to undo it. */}
          {hasColumnFilters(gridFilters) && (
            <button onClick={() => setGridFilters({})} title="Clear all filters" className="stui-clear-filter-btn">
              <FilterX size={16} /><span>{filteredPerms.length}/{matrix?.permissions?.length || 0}</span>
            </button>
          )}
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input className="stui-search-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search..." title={SEARCH_HINT} />
          </div>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                {viewMode === 'datasheet' && (
                  <>
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowColumnSettings(true); }}><SlidersHorizontal size={16} /><span>Modify View</span></button>
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); /* TODO: export */ }}><Download size={16} /><span>Export Excel</span></button>
                    {/* No bulk delete — permissions managed on the Permissions page */}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="stui-list-view">
          {listCollapsed ? (
            <div className="stui-list-panel stui-collapsed">
              <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
            </div>
          ) : (
            <div className="stui-list-panel">
              <div className="stui-list-panel-header">
                <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              </div>
              <div className="stui-list-scroll">
                {filteredPerms.length === 0 ? (
                  <div className="stui-list-empty">{matrix?.permissions?.length ? NO_MATCH_LIST : 'No permissions found'}</div>
                ) : filteredPerms.map(p => {
                  // Include the synthetic SysAdmin +1 in the count so admins see the full picture.
                  const grantCount = ROLE_NAMES.filter(r => (p.roles[r] || 'None') === 'Grant').length + 1;
                  return (
                    <button key={p.id} onClick={() => { setSelected(p); setEditing(null); setDirtyRoles({}); }} className={`stui-list-item ${selected?.id === p.id ? 'selected' : ''}`}>

                        <span className="stui-list-item-primary">{p.displayName || p.permissionKey}</span>
                        <span className="stui-list-item-secondary">{p.category} • {grantCount}/{DISPLAY_ROLE_NAMES.length} roles</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="stui-detail-panel">
            {selected ? (
              <div className="stui-detail-content">
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Permission Details</div></div>
                  <div className="stui-field-group stui-col-8"><label>Category</label><input className="stui-field-input" value={selected.category || ''} readOnly /></div>
                  <div className="stui-field-group stui-col-8"><label>Display Name</label><input className="stui-field-input" value={selected.displayName || ''} readOnly /></div>
                  <div className="stui-field-group stui-col-8"><label>Permission Key</label><input className="stui-field-input derived-field" value={selected.permissionKey} readOnly /></div>
                  <div className="stui-field-group stui-col-24"><label>Description</label><input className="stui-field-input" value={selected.description || ''} readOnly /></div>
                </div>
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Role Defaults</div></div>
                  {DISPLAY_ROLE_NAMES.map(r => {
                    const access = getRoleAccess(r);
                    const isDirty = dirtyRoles[r] !== undefined;
                    const synthetic = isSynthetic(r);
                    return (
                      <div key={r} className="stui-field-group stui-col-4">
                        <label>{r}{synthetic ? ' (flag)' : ''}</label>
                        {isGlobalOnly(selected) && !synthetic ? (
                          <input
                            className="stui-field-input"
                            value={GLOBAL_ONLY_LABEL}
                            readOnly
                            title="Granted per user under Global Permissions. A role default cannot grant it — a role is only ever held on one database."
                            style={{ fontStyle: 'italic', color: 'var(--color-gray-500)' }}
                          />
                        ) : isEditing && !synthetic ? (
                          <StuiSelect
                            className={`stui-field-input${isDirty ? ' dirty' : ''}`}
                            value={access === 'Grant' ? 'Grant' : 'Deny'}
                            onChange={e => {
                              const val = e.target.value;
                              const original = (selected.roles[r] || 'None') === 'Grant' ? 'Grant' : 'Deny';
                              const newDirty = { ...dirtyRoles };
                              if (val === original) { delete newDirty[r]; }
                              else { newDirty[r] = val === 'Grant' ? 'Grant' : 'None'; }
                              setDirtyRoles(newDirty);
                            }}
                          >
                            <option value="Grant" className="stui-access-grant stui-fw-semibold">Grant</option>
                            <option value="Deny" className="stui-access-deny stui-fw-semibold">Deny</option>
                          </StuiSelect>
                        ) : (
                          <input
                            className="stui-field-input"
                            value={access === 'Grant' ? 'Grant' : 'Deny'}
                            readOnly
                            title={synthetic ? 'SysAdmin bypass — flag on AppSec_Users, not editable here' : undefined}
                            style={{
                              ...accessStyle(access === 'Grant' ? 'Grant' : 'Deny'),
                              opacity: synthetic ? 0.75 : 1
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="stui-detail-empty">
                {(matrix?.permissions?.length || 0) > 0 && filteredPerms.length === 0 ? (
                  <NoMatchState hasSearch={!!searchTerm} hasFilters={hasColumnFilters(gridFilters)} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setGridFilters({})} />
                ) : (
                  <>
                    <Eye size={48} />
                    <p>Select a permission to view role defaults</p>
                    <p className="hint">Or switch to datasheet view for the full matrix</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="stui-datasheet-view stui-no-column-resize stui-roles-capabilities-datasheet" style={{ backgroundColor: 'var(--color-bg-white)' }}>
          <FilterableDataGrid
            data={gridData}
            allData={allGridData}
            columns={gridColumns}
            filters={gridFilters}
            sortConfig={gridSort || { key: null, direction: null }}
            onSortChange={setGridSort}
            setFilters={setGridFilters}
            columnOrder={columnOrder}
            hiddenColumns={hiddenColumns}
            columnWidths={{}}
            editModeEnabled={true}
            editedData={{}}
            renderEditableCell={renderEditableCell}
          />
        </div>
      )}
      <ColumnSettingsModal
        isOpen={showColumnSettings}
        onClose={() => setShowColumnSettings(false)}
        columns={gridColumns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        onApply={({ columnOrder: newOrder, hiddenColumns: newHidden }) => {
          setColumnOrder(newOrder);
          setHiddenColumns(newHidden);
          setShowColumnSettings(false);
        }}
      />
      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={SEARCH_FIELD_LABELS} onApply={setSearchTerm} />
    </div>
  );
};

export default RolesCapabilitiesPage;
